import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AppRoutes } from './app-routes';
import { loadMistakesFromCloud, saveMistakesToCloud } from './mistakes-cloud';
import { AchievementCelebrationCenter } from './components/AchievementCelebrationCenter';
import { Toaster } from './components/ui/sonner';
import { useLanguage } from './hooks/useLanguage';
import { processCompletedTrainingAchievements, type AchievementUnlockRecord } from './lib/achievements';
import {
  buildTrainingResultsState,
  type CompletedTrainingPayload,
  type TrainingResultsState,
} from './lib/trainingSession';
import { recordTrainingMistakes } from './mistakes-store';
import { useAuth } from './providers/AuthProvider';
import { saveTrainingSession } from './services/progress';
import type { TrainingSource } from './types';

type CompletedTrainingPayloadWithSource = CompletedTrainingPayload & { source: TrainingSource };
type RoutedTrainingResultsState = TrainingResultsState & { source: TrainingSource };

export default function App() {
  const [trainingResults, setTrainingResults] = useState<RoutedTrainingResultsState | null>(null);
  const [achievementUnlocks, setAchievementUnlocks] = useState<AchievementUnlockRecord[]>([]);

  const { t } = useLanguage();
  const { user, loading, signIn, signUp, signInWithGoogle, resetPassword, logout } = useAuth();

  const isAuthenticated = Boolean(user);

  useEffect(() => {
    if (!user?.uid) {
      return;
    }

    void loadMistakesFromCloud(user.uid).catch((error) => {
      console.error('Failed to load mistakes from cloud:', error);
    });
  }, [user?.uid]);

  const handleSignIn = async (email: string, password: string) => {
    await signIn(email, password);
    toast.success(t('app.toast.welcome'));
  };

  const handleSignUp = async (displayName: string, email: string, password: string) => {
    await signUp(displayName, email, password);
    toast.success(t('app.toast.signupSuccess'));
  };

  const handleGoogleSignIn = async () => {
    await signInWithGoogle();
    toast.success(t('app.toast.google'));
  };

  const handlePasswordReset = async (email: string) => {
    await resetPassword(email);
    toast.success(t('app.toast.passwordResetSent'));
  };

  const handleLogout = async () => {
    await logout();
    setTrainingResults(null);
    setAchievementUnlocks([]);
    toast.info(t('app.toast.logout'));
  };

  const handleTrainingComplete = (payload: CompletedTrainingPayloadWithSource) => {
    const resultsState: RoutedTrainingResultsState = {
      ...buildTrainingResultsState(payload),
      source: payload.source,
    };
    setTrainingResults(resultsState);

    if (!user?.uid) {
      return;
    }

    const completedAt = new Date();
    const durationMinutes = Math.max(0, Math.round(resultsState.totalTime / 60000));
    const sessionRecord = {
      id: `local-${completedAt.getTime()}-${payload.mode}`,
      userId: user.uid,
      mode: payload.mode,
      sessionMode: payload.sessionMode,
      accuracy: resultsState.accuracy,
      durationMinutes,
      verbsLearned: resultsState.verbsLearned,
      xpEarned: resultsState.xpEarned,
      completedAt,
    };

    void processCompletedTrainingAchievements({
      userId: user.uid,
      session: sessionRecord,
    })
      .then(({ unlocks }) => {
        setAchievementUnlocks(unlocks);
      })
      .catch((error) => {
        console.error('Failed to process achievements:', error);
      });

    try {
      const mistakeSync = recordTrainingMistakes({
        userId: user.uid,
        mode: payload.mode,
        results: resultsState.results,
      });

      if (mistakeSync.changed) {
        void saveMistakesToCloud(user.uid, mistakeSync.mistakes).catch((error) => {
          console.error('Failed to sync mistakes to cloud:', error);
        });
      }
    } catch (error) {
      console.error('Failed to save mistakes to localStorage:', error);
    }

    void saveTrainingSession({
      id: sessionRecord.id,
      userId: user.uid,
      mode: payload.mode,
      sessionMode: payload.sessionMode,
      accuracy: resultsState.accuracy,
      durationMinutes,
      verbsLearned: resultsState.verbsLearned,
      xpEarned: resultsState.xpEarned,
      completedAt,
    }).catch((error) => {
      console.error('Failed to save training session:', error);
      toast.error(t('app.toast.sessionSaveError'));
    });
  };

  const handleClearTrainingResults = () => {
    setTrainingResults(null);
    setAchievementUnlocks([]);
  };

  return (
    <>
      <AppRoutes
        loading={loading}
        isAuthenticated={isAuthenticated}
        activeUser={user}
        trainingResults={trainingResults}
        onSignIn={handleSignIn}
        onSignUp={handleSignUp}
        onGoogleSignIn={handleGoogleSignIn}
        onForgotPassword={handlePasswordReset}
        onLogout={handleLogout}
        onTrainingComplete={handleTrainingComplete}
        onClearTrainingResults={handleClearTrainingResults}
      />
      <AchievementCelebrationCenter unlocks={achievementUnlocks} onHandled={() => setAchievementUnlocks([])} />
      <Toaster />
    </>
  );
}
